import React, { useState, useEffect } from "react";
import { Container, Header, Tab, Button } from "semantic-ui-react";
import { mapValueToKey } from "utils/map-utils";
import {
  handleMessage,
  validateHostDetail,
  getColorStatus,
} from "utils/function-utils";
import { getHostList, getMES15ApiColor } from "services/developer";
import { PortalMessage } from "components/Admin/PortalMessage";
import { SearchBar } from "components/Admin/SearchBar";
import { HostFormModal } from "components/Admin/Hosts/HostFormModal";
import { DeleteHostModal } from "components/Admin/Hosts/DeleteHostModal";
import HostTable from "components/Admin/Hosts/HostTable";
import { configOptions, TIMEOUT_DURATION } from "env.config";

import {
  getMES16HostList,
  addHost,
  updateHost,
  deleteHost,
} from "services/developer";
import { PAGE_SIZE } from "env.config";

const TableActions = ({
  activeIndex,
  setOpenDeleteHost,
  selectedItem,
  handleFormModal,
}) => {
  if (activeIndex === 0) return null;
  return (
    <Button.Group style={{ float: "right" }}>
      <Button content="Add" onClick={() => handleFormModal("add")} />
      <Button
        content="Edit"
        disabled={!selectedItem || !selectedItem.HostId}
        onClick={() => handleFormModal("edit")}
      />
      <Button
        content="Delete"
        disabled={!selectedItem || !selectedItem.HostId}
        onClick={() => setOpenDeleteHost(true)}
      />
    </Button.Group>
  );
};

const HostAdmin = () => {
  const [MES15ListInfo, setMES15ListInfo] = useState({
    hostList: [],
    totalPages: 0,
    activePage: 0,
    loaded: false,
  });
  const [MES16ListInfo, setMES16ListInfo] = useState({
    hostList: [],
    totalPages: 0,
    activePage: 0,
    loaded: false,
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [editStatus, setEditStatus] = useState(false);
  const [openDeleteHost, setOpenDeleteHost] = useState(false);
  const [showSuccessMes, setShowSuccessMes] = useState("");
  const [showFailMes, setShowFailMes] = useState("");
  const [selectedItem, setSelectedItem] = useState({});
  const [formData, setFormData] = useState({});
  const [filter, setFilter] = useState({ column: "Host", value: "" });
  const [renderList, setRenderList] = useState([]);
  const [activeStatus, setActiveStatus] = useState({});
  useEffect(() => {
    (async () => {
      const sortedMES15List = await getMES15List();
      const sortedMES16List = await getMES16List();
      const MES15totalPages = Math.ceil(sortedMES15List.length / PAGE_SIZE);
      const MES16totalPages = Math.ceil(sortedMES16List.length / PAGE_SIZE);
      setRenderList(sortedMES15List);
      setMES15ListInfo({
        ...MES15ListInfo,
        hostList: sortedMES15List,
        totalPages: MES15totalPages,
        loaded: true,
      });
      setMES16ListInfo({
        ...MES16ListInfo,
        hostList: sortedMES16List,
        totalPages: MES16totalPages,
        loaded: true,
      });
      await getMES16Active(sortedMES16List);
    })();
  }, []);

  const getMES15List = async () => {
    const list = await getHostList();
    const reqList = list.map((item) =>
      getMES15ApiColor(item.Envide).then(
        (res) => res.active || (!res.active && "invalid")
      )
    );
    const colorList = await Promise.all(reqList);
    list.forEach((item, index) => {
      item.Active = colorList[index];
    });
    return sortColumnsByDefault(list);
  };

  const getMES16List = async () => {
    const list = await getMES16HostList();
    return sortColumnsByDefault(list);
  };

  const getMES16Active = async (list) => {
    const urls = list.map((item) => item.Host);
    const results = {};
    async function processQueue() {
      while (urls.length > 0) {
        const url = urls.shift();
        const result = await getColorStatus(TIMEOUT_DURATION, url);
        results[url] = result;
        setActiveStatus({ ...results });
      }
    }
    const requestPromises = Array.from({ length: 5 }, processQueue);
    await Promise.all(requestPromises);
  };

  const sortColumnsByDefault = (list) => {
    return list.sort((a, b) => {
      if (a.Scope !== b.Scope) {
        return a.Scope.localeCompare(b.Scope);
      } else {
        if (a.Stage !== b.Stage) {
          return a.Stage.localeCompare(b.Stage);
        } else {
          if (a.Name !== b.Name) {
            return a.Name.localeCompare(b.Name);
          }
        }
      }
      return a;
    });
  };

  const getInitFormData = (editStatus) => {
    if (editStatus) {
      const { Scope, Stage, Host, Name } = selectedItem;
      return {
        scope: Scope,
        scopeValid: true,
        stage: Stage,
        stageValid: true,
        host: Host,
        hostValid: true,
        name: Name,
        nameValid: true,
      };
    }
    return {
      scope: "",
      scopeValid: true,
      stage: "",
      stageValid: true,
      host: "",
      hostValid: true,
      name: "",
      nameValid: true,
    };
  };

  const onSelectHost = (data) => {
    if (data.MES === "MES16") {
      setSelectedItem(data);
    }
  };

  const handleFormModal = (modalStatus) => {
    const editStatus = modalStatus === "edit" ? true : false;
    setFormData(getInitFormData(editStatus));
    setEditStatus(editStatus);
    setOpenFormModal(true);
  };

  const onChangeFormItem = (formKey, value) => {
    if (formKey === "host") {
      value = value && value.trim();
    }
    const formKeyValid = formKey + "Valid";
    setFormData({ ...formData, [formKey]: value, [formKeyValid]: true });
  };

  const onUpdateHost = async (formData) => {
    const params = {
      HostId: selectedItem.HostId,
      Host: selectedItem.Host,
      Name: formData.name,
    };
    const res = await updateHost(params);
    if (res.success) {
      const sortedMES16List = await getMES16List();
      onJumpToSelectedItem(sortedMES16List, params.HostId);
    } else {
      setMES16ListInfo({ ...MES16ListInfo, loaded: true });
    }
    handleMessage({
      success: res.success,
      operation: "updateHost",
      content: res.message,
      setShowSuccessMes,
      setShowFailMes,
    });
  };

  const validateNotMES15Host = async () => {
    const hostList = await getHostList();
    const isHostExist = hostList.some((item) => item.Host === formData.host);
    const isNameExist = hostList.some((item) => item.Name === formData.name);
    if (isHostExist) {
      setMES16ListInfo({ ...MES16ListInfo, loaded: true });
      handleMessage({
        success: false,
        operation: "addHost",
        content: "The host already exists.",
        setShowSuccessMes,
        setShowFailMes,
      });
      return;
    }
    if (isNameExist) {
      setMES16ListInfo({ ...MES16ListInfo, loaded: true });
      handleMessage({
        success: false,
        operation: "addHost",
        content: "The alias already exists.",
        setShowSuccessMes,
        setShowFailMes,
      });
      return;
    }
    return true;
  };

  const validateHost = async (host) => {
    const result = await getColorStatus(TIMEOUT_DURATION, host);
    if (result !== "blue" && result !== "green") {
      handleMessage({
        success: false,
        operation: "addHost",
        content: "The host is unavailable for API Gateway.",
        setShowSuccessMes,
        setShowFailMes,
      });
      setMES16ListInfo({ ...MES16ListInfo, loaded: true });
      return;
    }
    return true;
  };
  const onJumpToSelectedItem = (hostList, hostId) => {
    const index = hostList.findIndex((host) => host.HostId === hostId);
    const curTotalPages = Math.ceil(hostList.length / PAGE_SIZE);
    const curActivePage = Math.floor(index / PAGE_SIZE);
    const renderList = hostList.slice(
      curActivePage * PAGE_SIZE,
      (curActivePage + 1) * PAGE_SIZE
    );
    const selectedItem = hostList[index];
    setFilter({ column: "Host", value: "" });
    setMES16ListInfo({
      ...MES16ListInfo,
      hostList,
      totalPages: curTotalPages,
      activePage: curActivePage,
      loaded: true,
    });
    setRenderList(renderList);
    setSelectedItem(selectedItem);
  };

  const onAddHost = async (formData) => {
    const { host, scope, stage, name } = formData;
    const keyParams = { scope, stage };
    const envide = await mapValueToKey(keyParams, true);
    const params = {
      HostId: Date.now().toString(),
      Host: host,
      Scope: scope,
      Stage: stage,
      Name: name,
      Envide: envide,
    };
    const res = await addHost(params);
    if (res.success) {
      const sortedMES16List = await getMES16List();
      const color = await getColorStatus(TIMEOUT_DURATION, host);
      setActiveStatus({ ...activeStatus, [host]: color });
      onJumpToSelectedItem(sortedMES16List, params.HostId);
    } else {
      setMES16ListInfo({ ...MES16ListInfo, loaded: true });
    }
    handleMessage({
      success: res.success,
      operation: "addHost",
      content: res.message,
      setShowSuccessMes,
      setShowFailMes,
    });
  };

  const onDeleteHost = async () => {
    setOpenDeleteHost(false);
    setMES16ListInfo({ ...MES16ListInfo, loaded: false });
    const params = {
      HostId: selectedItem.HostId,
      Host: selectedItem.Host,
    };
    const res = await deleteHost(params);
    if (res.success) {
      const sortedMES16List = await getMES16List();
      const curTotalPages = Math.ceil(sortedMES16List.length / PAGE_SIZE);
      setMES16ListInfo({
        ...MES16ListInfo,
        hostList: sortedMES16List,
        totalPages: curTotalPages,
        activePage: 0,
        loaded: true,
      });
      setRenderList(sortedMES16List);
      setSelectedItem({});
    } else {
      setMES16ListInfo({ ...MES16ListInfo, loaded: true });
    }
    handleMessage({
      success: res.success,
      operation: "deleteHost",
      content: res.message,
      setShowSuccessMes,
      setShowFailMes,
    });
  };

  const onCloseFormModal = () => {
    setOpenFormModal(false);
  };

  const handleSaveHost = async () => {
    const newFormData = validateHostDetail(formData);
    if (newFormData) {
      setFormData(newFormData);
      return;
    }
    setOpenFormModal(false);
    setMES16ListInfo({ ...MES16ListInfo, loaded: false });
    const isInvalid = await validateNotMES15Host();
    if (!isInvalid) return;
    if (editStatus) {
      onUpdateHost(formData);
    } else {
      const isHostValid = await validateHost(formData.host);
      isHostValid && onAddHost(formData);
    }
  };

  const onChangeFilter = (_e, data) => {
    const column = data.selection ? data.value : filter.column;
    const value = data.selection ? filter.value : data.value;
    const listInfo = activeIndex === 1 ? MES16ListInfo : MES15ListInfo;
    let availHostList = listInfo.hostList;
    if (column && value) {
      availHostList = availHostList.filter(
        (item) => item[column].toLowerCase().indexOf(value.trim().toLowerCase()) > -1
      );
    }
    const curTotalPages = Math.ceil(availHostList.length / PAGE_SIZE);
    activeIndex === 1
      ? setMES16ListInfo({
          ...MES16ListInfo,
          totalPages: curTotalPages,
          activePage: 0,
        })
      : setMES15ListInfo({
          ...MES15ListInfo,
          totalPages: curTotalPages,
          activePage: 0,
        });
    setRenderList(availHostList);
    setFilter({ column, value });
  };

  const onTabChange = (_e, { activeIndex }) => {
    if (!MES15ListInfo.loaded || !MES16ListInfo.loaded) return;
    const listInfo = activeIndex === 1 ? MES16ListInfo : MES15ListInfo;
    let filteredList = listInfo.hostList;
    let activePage = listInfo.activePage;
    if (filter.value) {
      filteredList = filteredList.filter(
        (item) =>
          item[filter.column]
            .toLowerCase()
            .indexOf(filter.value.toLowerCase()) > -1
      );
    }
    const curTotalPages = Math.ceil(filteredList.length / PAGE_SIZE);
    const renderList = filteredList.slice(
      activePage * PAGE_SIZE,
      (activePage + 1) * PAGE_SIZE
    );
    setActiveIndex(activeIndex);
    activeIndex === 1
      ? setMES16ListInfo({
          ...MES16ListInfo,
          totalPages: curTotalPages,
        })
      : setMES15ListInfo({
          ...MES15ListInfo,
          totalPages: curTotalPages,
        });
    setRenderList(renderList);
  };

  const onPageChange = (_e, data) => {
    const listInfo = activeIndex === 1 ? MES16ListInfo : MES15ListInfo;

    let filteredList = listInfo.hostList;
    const { column, value } = filter;
    if (column && value) {
      filteredList = filteredList.filter(
        (item) => item[column].toLowerCase().indexOf(value.toLowerCase()) > -1
      );
    }
    const renderList = filteredList.slice(
      (data.activePage - 1) * PAGE_SIZE,
      data.activePage * PAGE_SIZE
    );
    setRenderList(renderList);
    activeIndex === 1
      ? setMES16ListInfo({
          ...MES16ListInfo,
          activePage: data.activePage - 1,
        })
      : setMES15ListInfo({
          ...MES15ListInfo,
          activePage: data.activePage - 1,
        });
  };

  return (
    <Container fluid style={{ padding: "2em" }}>
      <Header as="h1">Hosts</Header>
      <PortalMessage
        showSuccessMes={showSuccessMes}
        showFailMes={showFailMes}
        setShowSuccessMes={setShowSuccessMes}
        setShowFailMes={setShowFailMes}
      />
      <SearchBar
        filter={filter}
        onChangeFilter={onChangeFilter}
        options={configOptions.hostFilter}
      />
      <TableActions
        activeIndex={activeIndex}
        handleFormModal={handleFormModal}
        setOpenDeleteHost={setOpenDeleteHost}
        selectedItem={selectedItem}
      />
      <Tab
        className="host-admin-tab"
        activeIndex={activeIndex}
        renderActiveOnly={false}
        onTabChange={onTabChange}
        panes={[
          {
            menuItem: "MES15",
            pane: (
              <Tab.Pane key="MES15">
                <HostTable
                  onPageChange={onPageChange}
                  renderList={renderList}
                  listInfo={MES15ListInfo}
                />
              </Tab.Pane>
            ),
          },
          {
            menuItem: "MES16",
            pane: (
              <Tab.Pane key="MES16">
                <HostTable
                  renderList={renderList}
                  onPageChange={onPageChange}
                  onSelectHost={onSelectHost}
                  selectedItem={selectedItem}
                  listInfo={MES16ListInfo}
                  activeStatus={activeStatus}
                />
                <HostFormModal
                  editStatus={editStatus}
                  openFormModal={openFormModal}
                  formData={formData}
                  onChangeFormItem={onChangeFormItem}
                  onCloseFormModal={onCloseFormModal}
                  onSaveHost={handleSaveHost}
                />
                <DeleteHostModal
                  openDeleteHost={openDeleteHost}
                  setOpenDeleteHost={setOpenDeleteHost}
                  selectedItem={selectedItem}
                  handleDeleteHost={onDeleteHost}
                />
              </Tab.Pane>
            ),
          },
        ]}
      />
    </Container>
  );
};

export default HostAdmin;
